import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { getSafetyPreparation, saveSafetyPreparation } from '../../../api/safetyAssessApi';
import '../Safety.css';

// 근로자 특성 체크 항목
const WORKER_CHAR_OPTIONS = [
  { key: 'female', label: '여성 근로자' },
  { key: 'unskilled', label: '미숙련 근로자' },
  { key: 'senior', label: '고령자(만 55세 이상)' },
  { key: 'foreigner', label: '외국인 근로자' },
  { key: 'disabled', label: '장애인 근로자' },
  { key: 'temporary', label: '일용·단기 근로자' },
];

function SafetyPreparation({ comCode = '' }) {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    accident_cnt: 0,
    near_miss_cnt: 0,
    has_work_env_measure: false,
    has_health_check: false,
    worker_characteristics: [],
    has_subcontract: false,
    has_msds: false,
    has_machine_inspection: false,
    need_special_education: false,
    need_work_permit: false,
  });

  const fetchData = useCallback(async () => {
    if (!token || !comCode) return;
    try {
      setLoading(true);
      const data = await getSafetyPreparation(token, comCode);
      if (data && data.id) {
        setFormData({
          accident_cnt: data.accident_cnt || 0,
          near_miss_cnt: data.near_miss_cnt || 0,
          has_work_env_measure: !!data.has_work_env_measure,
          has_health_check: !!data.has_health_check,
          worker_characteristics: (() => {
            try { return JSON.parse(data.worker_characteristics || '[]'); } catch { return []; }
          })(),
          has_subcontract: !!data.has_subcontract,
          has_msds: !!data.has_msds,
          has_machine_inspection: !!data.has_machine_inspection,
          need_special_education: !!data.need_special_education,
          need_work_permit: !!data.need_work_permit,
        });
      }
    } catch (err) {
      console.error('사전준비 마스터 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [token, comCode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumber = (field, value) => {
    // 문자열 강제 변환 후 숫자가 아닌 문자 제거
    const cleanValue = String(value).replace(/[^0-9]/g, '');
    const num = parseInt(cleanValue, 10);
    setFormData(prev => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };

  const handleWorkerChar = (key) => {
    setFormData(prev => {
      const arr = [...prev.worker_characteristics];
      const idx = arr.indexOf(key);
      if (idx > -1) arr.splice(idx, 1);
      else arr.push(key);
      return { ...prev, worker_characteristics: arr };
    });
  };

  const handleSave = async () => {
    if (!token || !comCode) return;
    try {
      setSaving(true);
      await saveSafetyPreparation({
        ...formData,
        worker_characteristics: JSON.stringify(formData.worker_characteristics),
      }, token, comCode);
      alert('사전준비 마스터 설정이 저장되었습니다.');
    } catch (err) {
      console.error('사전준비 마스터 저장 실패:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <div className="spinner-border" style={{ color: '#4f46e5' }} />
      </div>
    );
  }

  // 프리미엄 리스트 아이템 렌더링 헬퍼 (유/무 토글형)
  const renderToggleItem = (field, label, idx) => {
    const isYes = formData[field] === true;
    
    return (
      <div key={field} style={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
        padding: '8px 20px', borderBottom: '1px solid #f1f5f9',
        transition: 'background-color 0.2s'
      }}
      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            width: '24px', height: '24px', borderRadius: '6px', 
            background: '#e2e8f0', color: '#475569', fontWeight: '800', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' 
          }}>
            {idx}
          </div>
          <span style={{ fontSize: '0.93rem', fontWeight: '600', color: '#1e293b' }}>{label}</span>
        </div>
        
        <div style={{ 
          display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)'
        }}>
          <button 
            onClick={() => handleToggle(field, true)}
            style={{
              padding: '4px 14px', borderRadius: '6px', border: 'none',
              fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s',
              background: isYes ? '#4f46e5' : 'transparent',
              color: isYes ? '#fff' : '#64748b',
              boxShadow: isYes ? '0 2px 6px rgba(79, 70, 229, 0.2)' : 'none'
            }}
          >
            해당 (유)
          </button>
          <button 
            onClick={() => handleToggle(field, false)}
            style={{
              padding: '4px 14px', borderRadius: '6px', border: 'none',
              fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s',
              background: !isYes ? '#ef4444' : 'transparent',
              color: !isYes ? '#fff' : '#64748b',
              boxShadow: !isYes ? '0 2px 6px rgba(239, 68, 68, 0.2)' : 'none'
            }}
          >
            비해당 (무)
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '12px 0' }}>
      
      {/* 헤더 섹션 */}
      <div style={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '8px', paddingBottom: '6px', borderBottom: '2px solid #f1f5f9'
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', marginBottom: '4px', display: 'flex', alignItems: 'center' }}>
            기타 안전보건정보 (사전준비) 설정
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem', lineHeight: '1.4' }}>
            안전보건공단 표준 서식에 따른 사업장 안전보건 위험정보 10가지 항목입니다. 전사 공통 기본값으로 불러옵니다.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ 
            background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
            color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '8px',
            fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer',
            boxShadow: '0 6px 12px rgba(79, 70, 229, 0.2)',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-save2-fill" />}
          {saving ? '저장 중...' : '마스터 저장'}
        </button>
      </div>

      <div style={{ display: 'grid', gap: '16px' }}>
        
        {/* 그룹 1: 건수 입력 (1~2번) */}
        <div style={{ 
          background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', 
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', overflow: 'hidden' 
        }}>
          <div style={{ padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '700', color: '#334155', fontSize: '0.88rem' }}>
            재해 / 아차사고 발생 건수
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: '#e2e8f0', color: '#475569', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>1</div>
                <span style={{ fontSize: '0.93rem', fontWeight: '600', color: '#1e293b' }}>최근 3년간 재해발생 사례</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input 
                  type="number" 
                  min="0"
                  style={{ 
                    width: '70px', 
                    height: '32px', 
                    padding: '0 8px 0 4px', 
                    borderRadius: '6px', 
                    border: '1px solid #cbd5e1', 
                    textAlign: 'center', 
                    lineHeight: '30px',
                    fontWeight: '600', 
                    fontSize: '0.93rem', 
                    outline: 'none' 
                  }}
                  value={formData.accident_cnt}
                  onChange={e => handleNumber('accident_cnt', e.target.value)}
                />
                <span style={{ color: '#64748b', fontWeight: '600', fontSize: '0.88rem' }}>건</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: '#e2e8f0', color: '#475569', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>2</div>
                <span style={{ fontSize: '0.93rem', fontWeight: '600', color: '#1e293b' }}>아차사고 발생 사례</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input 
                  type="number" 
                  min="0"
                  style={{ 
                    width: '70px', 
                    height: '32px', 
                    padding: '0 8px 0 4px', 
                    borderRadius: '6px', 
                    border: '1px solid #cbd5e1', 
                    textAlign: 'center', 
                    lineHeight: '30px',
                    fontWeight: '600', 
                    fontSize: '0.93rem', 
                    outline: 'none' 
                  }}
                  value={formData.near_miss_cnt}
                  onChange={e => handleNumber('near_miss_cnt', e.target.value)}
                />
                <span style={{ color: '#64748b', fontWeight: '600', fontSize: '0.88rem' }}>건</span>
              </div>
            </div>
          </div>
        </div>

        {/* 그룹 2: 유/무 토글 체크 (3~10번) */}
        <div style={{ 
          background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', 
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', overflow: 'hidden' 
        }}>
          <div style={{ padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '700', color: '#334155', fontSize: '0.88rem' }}>
            점검 및 특성 항목
          </div>
          <div>
            {renderToggleItem('has_work_env_measure', '작업환경측정 결과', 3)}
            {renderToggleItem('has_health_check', '근로자 건강진단(특수건강진단) 결과', 4)}
            
            {/* 5번 근로자 특성 다중 선택 */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: '#e2e8f0', color: '#475569', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>5</div>
                <span style={{ fontSize: '0.93rem', fontWeight: '600', color: '#1e293b' }}>근로자 특성 <span style={{ fontSize: '0.8rem', color: '#94a3b8', marginLeft: '6px', fontWeight: '500' }}>(해당 항목 모두 선택)</span></span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingLeft: '36px' }}>
                {WORKER_CHAR_OPTIONS.map(opt => {
                  const isChecked = formData.worker_characteristics.includes(opt.key);
                  return (
                    <label key={opt.key} 
                      onClick={() => handleWorkerChar(opt.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '5px 12px', borderRadius: '99px', cursor: 'pointer',
                        border: isChecked ? '1px solid #4f46e5' : '1px solid #cbd5e1',
                        background: isChecked ? '#eef2ff' : '#fff',
                        color: isChecked ? '#4f46e5' : '#475569',
                        fontWeight: isChecked ? '700' : '500',
                        fontSize: '0.83rem', transition: 'all 0.15s'
                      }}
                    >
                      <div style={{ 
                        width: '14px', height: '14px', borderRadius: '3px', 
                        background: isChecked ? '#4f46e5' : '#fff', border: isChecked ? 'none' : '1.5px solid #cbd5e1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {isChecked && <i className="bi bi-check" style={{ color: '#fff', fontSize: '0.9rem', lineHeight: 0 }} />}
                      </div>
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {renderToggleItem('has_subcontract', '도급/용역/파견 사용 여부', 6)}
            {renderToggleItem('has_msds', 'MSDS(물질안전보건자료) 유해성 정보', 7)}
            {renderToggleItem('has_machine_inspection', '기계·기구 자체검사 결과', 8)}
            {renderToggleItem('need_special_education', '특별안전교육 필요 유무', 9)}
            {renderToggleItem('need_work_permit', '안전작업허가증 필요작업', 10)}
          </div>
        </div>

      </div>
    </div>
  );
}

export default SafetyPreparation;
